// src/lib/types/models.ts

// Maximum number of rows to process for LLM analysis
export const MAX_ROWS_FOR_LLM = 1000;

// LLM Provider type
export type LLMProvider = "openai" | "anthropic" | "gemini";
export type QualityTier = "better" | "cheaper";

// Token usage information
export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
}

// Cost information for models
export interface ModelCosts {
  inputCost: number; // Cost per 1M input tokens
  outputCost: number; // Cost per 1M output tokens
}

// Model definition - SINGLE SOURCE OF TRUTH
export interface ModelDefinition {
  id: string;
  displayName: string;
  provider: LLMProvider;
  costs: ModelCosts;
  qualityTier: QualityTier; // Added: defines if model is generally 'better' or 'cheaper' for its provider
}

// LLM model configuration (may not be strictly needed if selectedModelId is used directly)
export interface LLMConfig {
  provider: LLMProvider;
  model: string; // This is modelId
}

// Response from the API with token usage
export interface LLMResponse {
  content: string;
  usage: TokenUsage;
  modelId?: string;
  duration?: number; // Duration in milliseconds
}

// Common options interface (can be kept if used elsewhere)
export interface GenerateInsightsOptions {
  data: any[];
  sourceInfo: {
    name: string;
    filters: string;
    totalRows: number;
    rowsAnalyzed: number;
    outlierInfo: string;
  };
  prompt: string;
}

// Default provider can remain if used to set the initial provider in UI
export const DEFAULT_PROVIDER: LLMProvider = "openai";

export const AVAILABLE_MODELS: ModelDefinition[] = [
  // OpenAI Models (GPT-5 series)
  {
    id: "gpt-5-nano",
    displayName: "GPT-5 Nano",
    provider: "openai",
    costs: { inputCost: 0.05, outputCost: 0.4 },
    qualityTier: "cheaper",
  },
  {
    id: "gpt-5-mini",
    displayName: "GPT-5 Mini",
    provider: "openai",
    costs: { inputCost: 0.13, outputCost: 1.0 },
    qualityTier: "better",
  },
  // Anthropic Models
  // {
  //   id: "claude-3-5-haiku-latest",
  //   displayName: "Claude 3.5 Haiku",
  //   provider: "anthropic",
  //   costs: { inputCost: 0.8, outputCost: 4.0 },
  //   qualityTier: "cheaper",
  // },
  {
    id: "claude-haiku-4-5-20251001",
    displayName: "Claude 4.5 Haiku",
    provider: "anthropic",
    costs: { inputCost: 1.0, outputCost: 5.0 },
    qualityTier: "cheaper",
  },
  {
    id: "claude-sonnet-4-6",
    displayName: "Claude Sonnet 4.6",
    provider: "anthropic",
    costs: { inputCost: 3.0, outputCost: 15.0 },
    qualityTier: "better",
  },
  // Gemini Models (Gemini 3 only - supports web search)
  {
    id: "gemini-3-flash-preview",
    displayName: "Gemini 3 Flash",
    provider: "gemini",
    costs: { inputCost: 0.5, outputCost: 3.0 },
    qualityTier: "cheaper",
  },
  {
    id: "gemini-3.1-pro-preview",
    displayName: "Gemini 3.1 Pro",
    provider: "gemini",
    costs: { inputCost: 2.0, outputCost: 12.0 },
    qualityTier: "better",
  },
];

// Model names and pricing
// https://ai.google.dev/gemini-api/docs/models
// https://ai.google.dev/gemini-api/docs/pricing
// https://platform.openai.com/docs/models
// https://platform.openai.com/docs/pricing
// https://docs.anthropic.com/en/docs/about-claude/models/all-models
// https://docs.anthropic.com/en/docs/about-claude/pricing

// Get API Keys
// https://aistudio.google.com/app/apikey
// https://platform.openai.com/api-keys
// https://console.anthropic.com/settings/keys

// Helper function to get model by ID (remains useful)
export function getModelById(modelId: string): ModelDefinition | undefined {
  return AVAILABLE_MODELS.find((model) => model.id === modelId);
}

// New utility function to get a model ID based on provider and quality tier
export function getActualModelId(
  provider: LLMProvider,
  tier: QualityTier,
): string | undefined {
  const model = AVAILABLE_MODELS.find(
    (m) => m.provider === provider && m.qualityTier === tier,
  );
  // If multiple models fit criteria, this returns the first one.
  // If specific logic is needed (e.g. lowest cost cheaper), that could be added.
  return model?.id;
}

// New utility function to get the default model ID for a provider (which is its 'cheaper' tier)
export function getDefaultModelIdForProvider(
  provider: LLMProvider,
): string | undefined {
  return getActualModelId(provider, "cheaper");
}

// Helper function to calculate cost for token usage (remains useful)
export function calculateCost(
  usage: TokenUsage,
  modelId: string,
): number | undefined {
  const model = getModelById(modelId);
  if (!model) return undefined;

  const inputCost = (usage.inputTokens / 1000000) * model.costs.inputCost;
  const outputCost = (usage.outputTokens / 1000000) * model.costs.outputCost;

  return inputCost + outputCost;
}
